import React, { useRef, useEffect, useState } from 'react'
import { chunk } from 'lodash'
import dayjs from 'dayjs'

const keyCodeValues = {
  ArrowUp: -7,
  ArrowDown: 7,
  ArrowLeft: -1,
  ArrowRight: 1,
}

const Month = ({
  currentMonth,
  handleSelectDate,
  selectedDates = [],
  startDate,
  endDate,
  isOneWay,
}) => {
  const ref = useRef(null)
  const [focusedDate, setFocusedDate] = useState(null)
  const [hoverDate, setHoverDate] = useState(null)
  useEffect(() => {
    const handleNav = (e) => {
      if (e.key === 'Tab') {
        setFocusedDate(null)
        return
      }

      if (keyCodeValues[e.key] !== undefined) {
        const targetDate = dayjs(focusedDate)
          .add(keyCodeValues[e.key], 'days')
          .format('YYYY-MM-DD')

        const target = document.getElementById(targetDate)
        if (target) {
          e.preventDefault()
          target.focus()
          setFocusedDate(targetDate)
        }
      }
    }
    let currentRef
    if (ref.current && focusedDate) {
      // set listeners
      // focus first available date
      ref.current.addEventListener('keydown', handleNav)
      currentRef = ref.current
    }
    return () => {
      //  remove listeners
      if (currentRef) {
        currentRef.removeEventListener('keydown', handleNav)
      }
    }
  }, [focusedDate])

  const endOfMonth = currentMonth.endOf('month')
  // Next Month
  const prevMonth = currentMonth.subtract(1, 'month').startOf('month')
  const endOfPreviousMonth = prevMonth.endOf('month')

  const prevMonthDaysNeeded = +endOfPreviousMonth.format('d') + 1
  const firstPrevMonthDayNeeded = endOfPreviousMonth.subtract(
    prevMonthDaysNeeded - 1,
    'days'
  )

  const prevMonthDays = +currentMonth.format('d')
    ? Array.from({ length: prevMonthDaysNeeded }, (_, i) =>
        firstPrevMonthDayNeeded.add(i, 'days')
      )
    : []

  const daysInCurrentMonth = Array.from(
    { length: endOfMonth.format('DD') },
    (_, i) => currentMonth.add(i, 'days')
  )

  const nextMonthDays = Array.from(
    { length: 6 - endOfMonth.format('d') },
    (_, i) => endOfMonth.add(i + 1, 'days')
  )

  const weeks = chunk(
    [...prevMonthDays, ...daysInCurrentMonth, ...nextMonthDays],
    7
  )
  const header = Array.from({ length: 7 }, (_, i) =>
    dayjs().day(i).format('dd')
  )

  const handleSelectDay = (day) => {
    if (handleSelectDate) {
      handleSelectDate(day)
    }
  }

  const handleDayKeyDown = (e, day) => {
    if (e.keyCode === 32 || e.keyCode === 13) {
      e.preventDefault()
      handleSelectDay(day)
    }
  }

  const handleHoverDate = (day) => {
    if (selectedDates?.length === 1 && day.isAfter(startDate)) {
      setHoverDate(day.format('YYYY-MM-DD'))
    } else {
      setHoverDate(null)
    }
  }

  return (
    <div className="month" ref={ref}>
      <p className="title">{currentMonth.format('MMMM YYYY')}</p>
      <div className="row calendar-header">
        {header.map((day) => (
          <div key={day} className="item">
            {day}
          </div>
        ))}
      </div>
      {weeks.map((days, i) => (
        <div key={i} className="week row">
          {days.map((day) => {
            const isInCurrentMonth = day.isSame(currentMonth, 'month')

            const isFirstAvailableDay =
              isInCurrentMonth &&
              (day.isSame(dayjs(), 'day') ||
                (day.isAfter(dayjs(), 'day') && +day.date() === 1))

            const isSelected = selectedDates.some(
              (date) => date === day.format('YYYY-MM-DD')
            )

            const isDisabled = day.isBefore(dayjs(), 'days')

            const isToday = day.isSame(dayjs(), 'day')

            const isPreselected =
              hoverDate &&
              day.isAfter(startDate, 'day') &&
              (day.isBefore(hoverDate, 'day') ||
                day.isSame(hoverDate, 'day')) &&
              !endDate &&
              !isOneWay
            return (
              <div
                id={isInCurrentMonth ? `${day.format('YYYY-MM-DD')}` : ''}
                className={`item day ${
                  isInCurrentMonth ? 'current-month' : 'outside'
                } ${isSelected ? 'selected' : ''} ${
                  isDisabled ? 'disabled' : ''
                } ${isToday ? 'today' : ''} ${
                  isPreselected ? 'preselected' : ''
                }`}
                role="button"
                key={day.format()}
                aria-disabled={isDisabled}
                tabIndex={isFirstAvailableDay ? 0 : -1}
                onFocus={() => setFocusedDate(day.format('YYYY-MM-DD'))}
                onClick={() => (!isDisabled ? handleSelectDay(day) : null)}
                onKeyDown={(e) =>
                  !isDisabled ? handleDayKeyDown(e, day) : null
                }
                onMouseOver={() => handleHoverDate(day)}
                onMouseLeave={() => setHoverDate(null)}
              >
                {day.format('D')}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default Month
